import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import { apiFetch } from "../../api";
import { getUserContext } from "../../auth";

function useVehicleOptions() {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef(null);

  const fetchOptions = React.useCallback(async (q) => {
    if (controllerRef.current) controllerRef.current.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;
    try {
      setLoading(true);
      const res = await apiFetch(`/api/vehiculos/?q=${encodeURIComponent(q || "")}`, {
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error("no response");
      const data = await res.json();
      setOptions(
        (Array.isArray(data) ? data : [])
          .map((x) => {
            if (typeof x === "string") return { id: null, label: x };
            return { id: x.id ?? null, label: x.modelo || x.nombre || x.name };
          })
          .filter((o) => o && o.label)
      );
    } catch (e) {
      if (e.name !== "AbortError") setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, fetchOptions, loading };
}

function useStaticOptions(list) {
  const [options, setOptions] = React.useState(list.map((x, i) => ({ id: i, label: x })));

  const fetchOptions = React.useCallback((q) => {
    const query = String(q || "").trim().toLowerCase();
    const mapped = list.map((x, i) => ({ id: i, label: x }));
    setOptions(query ? mapped.filter((o) => o.label.toLowerCase().includes(query)) : mapped);
  }, [list]);

  return { options, fetchOptions };
}

const OBTENIDO_LIST = [
  "Meta Ads",
  "Messenger",
  "Instagram",
  "Facebook",
  "Contacto Directo",
  "Otro",
];

const TOPICO_LABELS = {
  INICIO: "Inicio",
  INFORMACION: "Información",
  PRECIO: "Precio",
  NEGOCIACION: "Negociación",
  SEGUIMIENTO: "Seguimiento",
  INTERES: "Interés",
};

const DEVOLUCION_TYPE_LABELS = {
  MANUAL: "Manual",
  CAMBIO_ESTADO: "Cambio de estado",
  FUTURO_PROGRAMADO: "Futuro programado",
  REASIGNACION: "Reasignación",
  REACTIVACION: "Reactivación",
  VENTA_REGISTRADA: "Venta registrada",
};

const DEVOLUCION_TYPE_COLORS = {
  MANUAL: { bg: "rgba(33, 150, 243, 0.08)", border: "rgba(33, 150, 243, 0.3)", chipBg: "rgba(33, 150, 243, 0.2)", chipColor: "#1565c0" },
  CAMBIO_ESTADO: { bg: "#fff7ed", border: "#fed7aa", chipBg: "#ffedd5", chipColor: "#c2410c" },
  FUTURO_PROGRAMADO: { bg: "#f5f3ff", border: "#ddd6fe", chipBg: "#ede9fe", chipColor: "#6d28d9" },
  REASIGNACION: { bg: "#ecfeff", border: "#a5f3fc", chipBg: "#cffafe", chipColor: "#0e7490" },
  REACTIVACION: { bg: "#f0fdf4", border: "#bbf7d0", chipBg: "#dcfce7", chipColor: "#15803d" },
  VENTA_REGISTRADA: { bg: "#fdf2f8", border: "#fbcfe8", chipBg: "#fce7f3", chipColor: "#be185d" },
};

const statusColor = (estado) => {
  if (estado === "ACTIVO") return "#2f9e44";
  if (estado === "VENTA") return "#2f9e44";
  if (estado === "BAJA") return "#e03131";
  if (estado === "FUTURO") return "#8a94a6";
  return "#8a94a6";
};

const carStatusColor = (estado) => {
  if (estado === "ACTIVO") return "#0059f6";
  if (estado === "VENTA") return "#2f9e44";
  if (estado === "BAJA") return "#e03131";
  if (estado === "FUTURO") return "#8a94a6";
  return "#8a94a6";
};

const statusPriority = (estado) => {
  if (estado === "ACTIVO") return 0;
  if (estado === "FUTURO") return 1;
  if (estado === "VENTA") return 2;
  if (estado === "BAJA") return 3;
  return 4;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeDevolucionTipo = (tipo) => String(tipo || "MANUAL").toUpperCase();

const formatTopico = (topico) => {
  const key = String(topico || "").toUpperCase();
  return TOPICO_LABELS[key] || String(topico || "");
};

const getHistorialMeta = (item) => {
  const tipo = normalizeDevolucionTipo(item?.tipo);
  const colors = DEVOLUCION_TYPE_COLORS[tipo] || DEVOLUCION_TYPE_COLORS.MANUAL;
  const label = tipo === "MANUAL"
    ? (item?.topico ? formatTopico(item.topico) : "Sin estadio")
    : (DEVOLUCION_TYPE_LABELS[tipo] || tipo.replaceAll("_", " ").toLowerCase());
  return { tipo, label, colors };
};

const consultaDateValue = (consulta) => {
  const value = consulta?.fecha_consulta || consulta?.fecha_inicio || consulta?.fecha_cierre || consulta?.fecha_programada;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const statusDetail = (consulta) => {
  if (consulta?.estado === "BAJA") return `Se cerró ${formatDate(consulta.fecha_cierre)}`;
  if (consulta?.estado === "VENTA") return `Se cerró ${formatDate(consulta.fecha_cierre)}`;
  if (consulta?.estado === "FUTURO") return `Programado para ${formatDate(consulta.fecha_programada)}`;
  return null;
};

const latestDate = (consultas, historial) => {
  const values = [
    ...(consultas || []).map((c) => c.fecha_consulta || c.fecha_inicio),
    ...(historial || []).map((h) => h.fecha),
  ].filter(Boolean);
  if (values.length === 0) return null;
  return values.sort((a, b) => new Date(b) - new Date(a))[0];
};

const EDITABLE_FIELDS = ["nombre", "email", "obtenido_de", "observacion"];

const getEditableSnapshot = (cliente) => ({
  nombre: cliente?.nombre || "",
  email: cliente?.email || "",
  obtenido_de: cliente?.obtenido_de || "",
  observacion: cliente?.observacion || "",
});

const normalizeEditableValue = (value) => String(value || "").trim();

const uniqueNonEmpty = (values) => {
  const seen = new Set();
  return values
    .map((value) => String(value || "").trim())
    .filter((value) => {
      if (!value) return false;
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

function InfoRow({ icon, label, value }) {
  const hasValue = value !== null && value !== undefined && String(value).trim() !== "";
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 1, alignItems: "center", py: 0.7, borderBottom: "1px solid #edf2f7" }}>
      <Box sx={{ color: "#6d7da0", display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: "#6d7da0", fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, color: hasValue ? "#172033" : "#9aa7bc", fontWeight: hasValue ? 800 : 600, overflow: "hidden", textOverflow: "ellipsis" }}>
          {hasValue ? value : "Sin datos"}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ClienteDialog({ open, onClose, cliente, onChange, onSave, onOpenSeguimiento, saving, loading = false }) {
  const [tab, setTab] = React.useState("consultas");
  const [localError, setLocalError] = React.useState(null);
  const [consultaInput, setConsultaInput] = React.useState("");
  const [creatingConsulta, setCreatingConsulta] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editForm, setEditForm] = React.useState(getEditableSnapshot(null));
  const [historial, setHistorial] = React.useState([]);
  const [historialPage, setHistorialPage] = React.useState(1);
  const [historialTotal, setHistorialTotal] = React.useState(0);
  const [historialLoading, setHistorialLoading] = React.useState(false);
  const vehicleApi = useVehicleOptions();
  const obtenidoApi = useStaticOptions(OBTENIDO_LIST);

  const consultas = React.useMemo(() => {
    const items = Array.isArray(cliente?.consultas) ? cliente.consultas : [];
    return [...items].sort((a, b) => {
      const priorityDiff = statusPriority(a.estado) - statusPriority(b.estado);
      if (priorityDiff !== 0) return priorityDiff;
      return consultaDateValue(b) - consultaDateValue(a);
    });
  }, [cliente]);
  const clienteNombre = cliente?.nombre || "";
  const clienteEmail = cliente?.email || "";
  const clienteOrigen = cliente?.obtenido_de || "";
  const clienteObservacion = cliente?.observacion || "";
  const editableSnapshot = React.useMemo(
    () => ({
      nombre: clienteNombre,
      email: clienteEmail,
      obtenido_de: clienteOrigen,
      observacion: clienteObservacion,
    }),
    [clienteNombre, clienteEmail, clienteOrigen, clienteObservacion]
  );

  React.useEffect(() => {
    if (open) {
      setTab("consultas");
      setLocalError(null);
      setConsultaInput("");
      setHistorial([]);
      setHistorialPage(1);
      setHistorialTotal(0);
      setIsEditing(false);
      setEditForm(editableSnapshot);
    }
  }, [open, cliente?.id, editableSnapshot]);

  React.useEffect(() => {
    if (!open || isEditing) return;
    setEditForm(editableSnapshot);
  }, [open, isEditing, editableSnapshot]);

  const loadHistorial = React.useCallback(async (page = 1, append = false) => {
    if (!cliente?.id) return;
    setHistorialLoading(true);
    try {
      const res = await apiFetch(`/api/clientes/${cliente.id}/historial?page=${page}&limit=20`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      setHistorial((prev) => append ? [...prev, ...items] : items);
      setHistorialPage(data.page || page);
      setHistorialTotal(data.total || items.length);
    } catch (e) {
      setLocalError("No se pudo cargar el historial.");
    } finally {
      setHistorialLoading(false);
    }
  }, [cliente?.id]);

  React.useEffect(() => {
    if (open && tab === "historial" && cliente?.id && historial.length === 0) {
      loadHistorial(1, false);
    }
  }, [open, tab, cliente?.id, historial.length, loadHistorial]);

  if (open && loading && !cliente) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 900 }}>Cliente</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <LinearProgress />
        <DialogContent sx={{ display: "flex", minHeight: 220, alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!cliente) return null;

  const disabled = Boolean(loading || saving || creatingConsulta);
  const activeConsultas = consultas.filter((c) => c.estado === "ACTIVO");
  const latestConsulta = consultas.find((c) => c.estado === "ACTIVO") || consultas[0] || null;
  const activeWithSeller = consultas.find((c) => c.estado === "ACTIVO" && c.id_vendedor) || consultas.find((c) => c.id_vendedor);
  const activeVehicleNames = uniqueNonEmpty(activeConsultas.map((c) => c.vehiculo_consultado));
  const activeSellerNames = uniqueNonEmpty(
    activeConsultas.map((c) => c.vendedor_nombre || (c.id_vendedor ? `Vendedor #${c.id_vendedor}` : ""))
  );
  const interesActual = activeVehicleNames.length > 0 ? activeVehicleNames.join(", ") : latestConsulta?.vehiculo_consultado;
  const vendedorActual = activeSellerNames.length > 0
    ? activeSellerNames.join(", ")
    : activeWithSeller?.vendedor_nombre || (activeWithSeller?.id_vendedor ? `Vendedor #${activeWithSeller.id_vendedor}` : "Sin asignar");
  const ultimaActividad = latestDate(consultas, historial);
  const estadoActual = latestConsulta?.estado || "Sin consultas";
  const currentUser = getUserContext();
  const isAdmin = currentUser?.rol === "Admin";
  const currentVendedorId = currentUser?.vendedor_id ?? currentUser?.id_vendedor ?? null;
  const hasEditableChanges = EDITABLE_FIELDS.some(
    (field) => normalizeEditableValue(editForm[field]) !== normalizeEditableValue(editableSnapshot[field])
  );
  const canSaveBasicData = isEditing && hasEditableChanges && !disabled;

  const setEditField = (field) => (eventOrValue) => {
    const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    setEditForm((prev) => ({ ...prev, [field]: value || "" }));
  };

  const saveBasicData = async () => {
    if (!isEditing) {
      setIsEditing(true);
      setLocalError(null);
      return;
    }
    if (!editForm.nombre || String(editForm.nombre).trim() === "") {
      setLocalError('El campo "Nombre" es obligatorio.');
      return;
    }
    if (!hasEditableChanges) return;
    setLocalError(null);
    const payload = {
      ...cliente,
      nombre: editForm.nombre.trim(),
      email: editForm.email.trim() || null,
      obtenido_de: editForm.obtenido_de.trim() || null,
      observacion: editForm.observacion.trim() || null,
    };
    try {
      const updated = await onSave?.(payload);
      if (updated !== null) {
        setIsEditing(false);
      }
    } catch (e) {
      setLocalError(e.message || "No se pudieron guardar los cambios.");
    }
  };

  const cancelEdit = () => {
    setEditForm(editableSnapshot);
    setIsEditing(false);
    setLocalError(null);
  };

  const openSeguimiento = (consulta, idx) => {
    const idVenCli = consulta?.id_ven_cli ?? consulta?.id;
    if (!idVenCli) return;
    const asignacion = {
      ...consulta,
      id: idVenCli,
      id_ven_cli: idVenCli,
      id_cliente: consulta.id_cliente || cliente.id,
      cliente_id: consulta.id_cliente || cliente.id,
      nombreCliente: cliente.nombre,
      clienteNombre: cliente.nombre,
      consulta: consulta.vehiculo_consultado,
      estado: consulta.estado || "ACTIVO",
      estadoVenta: consulta.estado || "ACTIVO",
      id_vendedor: consulta.id_vendedor,
      vendedor_id: consulta.id_vendedor,
      nombreVendedor: consulta.vendedor_nombre,
      vendedor_nombre: consulta.vendedor_nombre,
      estadioActual: consulta.estadioActual || consulta.estadio || "INICIO",
      _fromClienteDialog: true,
      _consultaIndex: idx,
    };
    onClose?.();
    onOpenSeguimiento?.(asignacion);
  };

  const openConsultaNoAsignada = (consulta) => {
    const consultaId = consulta?.id_ven_cli ?? consulta?.id;
    if (!consultaId) return;
    window.dispatchEvent(new CustomEvent("openConsultasNoAsignadas", {
      detail: { highlightConsultaId: Number(consultaId) },
    }));
    onClose?.();
  };

  const createConsulta = async () => {
    const value = consultaInput.trim();
    if (!value) {
      setLocalError("Elegí o escribí un vehículo consultado.");
      return;
    }
    setCreatingConsulta(true);
    setLocalError(null);
    try {
      const res = await apiFetch(`/api/clientes/${cliente.id}/consultas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vehiculo_consultado: value }),
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const nueva = await res.json();
      onChange?.({ ...cliente, consultas: [nueva, ...consultas], consulta: nueva.vehiculo_consultado });
      setConsultaInput("");
    } catch (e) {
      setLocalError(e.message || "No se pudo crear la consulta.");
    } finally {
      setCreatingConsulta(false);
    }
  };

  const showMoreHistorial = () => {
    if (historialLoading || historial.length >= historialTotal) return;
    loadHistorial(historialPage + 1, true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "10px",
          maxHeight: "calc(100vh - 48px)",
          width: "min(1000px, calc(100vw - 48px))",
        },
      }}
    >
      <DialogTitle sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: 12, color: "#4c5d86", fontWeight: 700 }}>Cliente</Typography>
            <Typography sx={{ fontSize: 26, lineHeight: 1.1, color: "#111827", fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis" }}>
              {(isEditing ? editForm.nombre : cliente.nombre) || "Cliente"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", px: 2, py: 1, minWidth: 100, height: 50, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 900 }}>{consultas.length}</Typography>
              <Typography sx={{ fontSize: 11, color: "#4c5d86" }}>consultas</Typography>
            </Box>
            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", px: 2, py: 1, minWidth: 100, height: 50, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontSize: 11, color: "#4c5d86" }}>Última actividad</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{formatDate(ultimaActividad)}</Typography>
            </Box>
            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", px: 2, py: 1, minWidth: 100, height: 50, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontSize: 11, color: "#4c5d86" }}>Estado actual</Typography>
              <Chip size="small" label={estadoActual} sx={{ height: 22, mt: 0.3, color: statusColor(estadoActual), bgcolor: `${statusColor(estadoActual)}18`, fontWeight: 800 }} />
            </Box>
            <IconButton onClick={onClose} disabled={disabled} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {(loading || saving) && <LinearProgress />}

      <DialogContent sx={{ px: 2, pb: 2.5, overflow: "hidden" }}>
        {localError && <Alert severity="error" sx={{ mb: 2 }}>{localError}</Alert>}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "330px minmax(0, 1fr)" }, gap: 1.5, height: { xs: "auto", md: "calc(100vh - 185px)" }, minHeight: 0 }}>
          <Stack spacing={1} sx={{ alignSelf: "stretch", minHeight: 0, overflowY: "auto", pr: 0.5 }}>
            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 1 }}>
              <Typography sx={{ fontWeight: 900, color: "#06185f", mb: 1 }}>Información básica</Typography>
              {isEditing && (
                <TextField
                  size="small"
                  label="Nombre"
                  value={editForm.nombre}
                  onChange={setEditField("nombre")}
                  disabled={disabled}
                  fullWidth
                  sx={{ mb: 1 }}
                />
              )}
              <InfoRow icon={<PhoneOutlinedIcon fontSize="small" />} label="Teléfono" value={cliente.telefono} />
              {isEditing ? (
                <TextField
                  size="small"
                  label="Email"
                  value={editForm.email}
                  onChange={setEditField("email")}
                  disabled={disabled}
                  fullWidth
                  sx={{ my: 1 }}
                />
              ) : (
                <InfoRow icon={<MailOutlineIcon fontSize="small" />} label="Email" value={cliente.email} />
              )}
              {isEditing ? (
                <Autocomplete
                  freeSolo
                  options={obtenidoApi.options}
                  getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                  value={obtenidoApi.options.find((o) => o.label === editForm.obtenido_de) || editForm.obtenido_de || null}
                  inputValue={editForm.obtenido_de}
                  onOpen={() => obtenidoApi.fetchOptions("")}
                  onInputChange={(e, value) => setEditField("obtenido_de")(value)}
                  onChange={(e, value) => setEditField("obtenido_de")(typeof value === "string" ? value : value?.label || "")}
                  renderInput={(params) => <TextField {...params} size="small" label="Origen" disabled={disabled} />}
                  sx={{ mb: 1 }}
                />
              ) : (
                <InfoRow icon={<SourceOutlinedIcon fontSize="small" />} label="Origen" value={cliente.obtenido_de} />
              )}
              <InfoRow icon={<DirectionsCarIcon fontSize="small" />} label="Interés actual" value={interesActual} />
              <InfoRow icon={<PersonOutlineIcon fontSize="small" />} label="Vendedor actual" value={vendedorActual} />

              {isEditing ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
                  <Button variant="outlined" size="small" onClick={cancelEdit} disabled={disabled} sx={{ fontWeight: 800, fontSize: 11 }}>
                    Cancelar
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={saveBasicData}
                    disabled={!canSaveBasicData}
                    startIcon={<SaveOutlinedIcon />}
                    sx={{ fontWeight: 800, fontSize: 11 }}
                  >
                    {saving ? "Guardando..." : "Guardar cambios"}
                  </Button>
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={saveBasicData}
                  disabled={disabled}
                  startIcon={<EditOutlinedIcon />}
                  sx={{ mt: 1, fontWeight: 800, fontSize: 12 }}
                >
                  Editar datos
                </Button>
              )}
            </Box>

            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 1.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ color: "#0059f6", display: "flex", pt: 0.2 }}>
                  <NotesOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, color: "#4c5d86", fontWeight: 900 }}>Observación</Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editForm.observacion}
                      onChange={setEditField("observacion")}
                      disabled={disabled}
                      fullWidth
                      multiline
                      minRows={3}
                      sx={{ mt: 0.8 }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: 13, color: "#172033", mt: 0.3, whiteSpace: "pre-wrap" }}>
                      {cliente.observacion || "Sin observación"}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Stack>

          <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 1.2, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box sx={{ display: "inline-flex", alignSelf: "flex-start", gap: 0.4, mb: 1.5, p: 0.35, border: "1px solid #d8e5f7", borderRadius: "8px", bgcolor: "#f8fbff" }}>
              {[
                { key: "consultas", label: "Consultas", icon: <DirectionsCarIcon sx={{ fontSize: 16 }} /> },
                { key: "historial", label: "Historial completo", icon: <HistoryIcon sx={{ fontSize: 16 }} /> },
              ].map((option) => {
                const selected = tab === option.key;
                return (
                  <Button
                    key={option.key}
                    size="small"
                    onClick={() => setTab(option.key)}
                    startIcon={option.icon}
                    sx={{
                      borderRadius: "6px",
                      px: 1.2,
                      py: 0.45,
                      minHeight: 30,
                      color: selected ? "#0059f6" : "#50617f",
                      bgcolor: selected ? "#fff" : "transparent",
                      border: selected ? "1px solid #0059f6" : "1px solid transparent",
                      boxShadow: selected ? "0 1px 4px rgba(0, 89, 246, 0.12)" : "none",
                      fontSize: 12,
                      fontWeight: 900,
                      "&:hover": {
                        bgcolor: selected ? "#fff" : "#eef5ff",
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
            </Box>

            {tab === "consultas" ? (
              <Stack spacing={1.2} sx={{ minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                {consultas.length === 0 ? (
                  <Box sx={{ border: "1px dashed #c9d7ea", borderRadius: "8px", p: 2, textAlign: "center", color: "#4c5d86" }}>
                    No hay consultas cargadas para este cliente.
                  </Box>
                ) : (
                  consultas.map((consulta, idx) => (
                    <Box key={consulta.id || idx} sx={{ display: "grid", gridTemplateColumns: { xs: "36px 1fr", md: "34px minmax(0, 1fr) 120px auto" }, gap: 1, alignItems: "center", border: "1px solid #e4ebf5", borderRadius: "8px", p: 1, borderLeft: consulta.estado === "ACTIVO" ? "3px solid #0059f6" : "1px solid #e4ebf5" }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: `${carStatusColor(consulta.estado)}14`, color: carStatusColor(consulta.estado), display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <DirectionsCarIcon sx={{ fontSize: 17 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: "#111827" }}>{consulta.vehiculo_consultado || "Sin vehículo"}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 12, color: "#4c5d86" }}>
                            {consulta.estado === "ACTIVO" ? "Actual" : "Anterior"} · {consulta.vendedor_nombre || (consulta.id_vendedor ? `Vendedor #${consulta.id_vendedor}` : "Sin asignar")}
                          </Typography>
                          {isAdmin && !consulta.id_vendedor && (
                            <Tooltip title="Esta consulta se marcará en la tabla de consultas no asignadas" arrow>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => openConsultaNoAsignada(consulta)}
                                sx={{ minWidth: 0, px: 0.6, py: 0, fontSize: 11, fontWeight: 900 }}
                              >
                                Ver
                              </Button>
                            </Tooltip>
                          )}
                          {currentVendedorId != null && consulta.id_vendedor != null && String(consulta.id_vendedor) === String(currentVendedorId) && (
                            <Chip size="small" label="Yo" sx={{ height: 18, bgcolor: "#e8f2ff", color: "#0059f6", fontSize: 10, fontWeight: 900 }} />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ minWidth: 0, gridColumn: { xs: "2", md: "auto" } }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: statusColor(consulta.estado), flex: "0 0 auto" }} />
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#172033" }}>{consulta.estado}</Typography>
                        </Box>
                        {statusDetail(consulta) && (
                          <Typography sx={{ fontSize: 10.5, color: "#6d7da0", mt: 0.2 }}>
                            {statusDetail(consulta)}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, gridColumn: { xs: "2", md: "auto" } }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openSeguimiento(consulta, idx)}
                          endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                          sx={{ minWidth: 136, fontSize: 10.5, fontWeight: 800 }}
                        >
                          Ver seguimiento
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}

                <Divider />

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={vehicleApi.options}
                    getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                    inputValue={consultaInput}
                    onInputChange={(e, value, reason) => {
                      setConsultaInput(value);
                      if (reason === "input") vehicleApi.fetchOptions(value);
                    }}
                    onOpen={() => vehicleApi.fetchOptions("")}
                    onChange={(e, value) => setConsultaInput(typeof value === "string" ? value : value?.label || "")}
                    loading={vehicleApi.loading}
                    renderInput={(params) => <TextField {...params} size="small" label="Vehículo consultado" disabled={disabled} />}
                  />
                  <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={createConsulta} disabled={disabled}>
                    Nueva consulta
                  </Button>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1.2} sx={{ minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                {historialLoading && historial.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
                ) : historial.length === 0 ? (
                  <Box sx={{ border: "1px dashed #c9d7ea", borderRadius: "8px", p: 2, textAlign: "center", color: "#4c5d86" }}>
                    No hay devoluciones registradas.
                  </Box>
                ) : (
                  historial.map((item) => {
                    const meta = getHistorialMeta(item);
                    return (
                      <Box
                        key={item.id}
                        sx={{
                          border: `1px solid ${meta.colors.border}`,
                          borderRadius: "8px",
                          p: 1.2,
                          bgcolor: meta.colors.bg,
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" },
                          gap: 1,
                          alignItems: "start",
                        }}
                      >
                        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.15 }}>
                            {item.vehiculo_consultado || "Consulta"}
                          </Typography>
                          <Chip
                            size="small"
                            label={meta.label}
                            sx={{
                              height: 21,
                              borderRadius: "6px",
                              bgcolor: meta.colors.chipBg,
                              color: meta.colors.chipColor,
                              fontSize: 10.5,
                              fontWeight: 900,
                              "& .MuiChip-label": { px: 0.8 },
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 12, color: "#6d7da0", fontWeight: 700, textAlign: { xs: "left", sm: "right" } }}>
                          {formatDateTime(item.fecha)}
                        </Typography>
                        <Typography sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, fontSize: 13, color: "#24345d", fontWeight: 700, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                          {item.devolucion || "Sin mensaje"}
                        </Typography>
                      </Box>
                    );
                  })
                )}
                {historial.length < historialTotal && (
                  <Button onClick={showMoreHistorial} disabled={historialLoading} variant="outlined">
                    {historialLoading ? "Cargando..." : "Ver más"}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
